import * as React from 'react';

import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { DexMultiTileModel, DexTileModel } from 'models/common/ads-types';

import { UitkCard, UitkCardLink } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';
import { UitkSpacing, UitkSpacingHr } from 'uitk-react-spacing';
import { UitkSponsoredBadge } from 'uitk-react-badge';

interface TileFigureProps {
  tile: DexTileModel;
  ratio: UitkFigureAspectRatioType;
}

const TileFigure: React.FunctionComponent<TileFigureProps> = ({ tile, ratio }) => (
  <UitkFigure ratio={ratio}>
    <UitkImage alt={tile.imageAltText} src={tile.image} />
    <UitkScrim type={UitkScrimType.BOTTOM}>
      <UitkHeading tag="h3" size={6}>
        {tile.header}
      </UitkHeading>
    </UitkScrim>
  </UitkFigure>
);

TileFigure.displayName = 'TileFigure';

interface TileCardProps {
  tile: DexTileModel;
  index: number;
}

const TileCard: React.FunctionComponent<TileCardProps> = ({ tile, index }) => {
  const label = `${tile.ariaLabel} ${index}`;

  return (
    <UitkLayoutGridItem key={index}>
      <UitkCard>
        <Viewport>
          <ViewSmall>
            <TileFigure tile={tile} ratio={UitkFigureAspectRatioType.R3_4} />
          </ViewSmall>
          <ViewMedium>
            <TileFigure tile={tile} ratio={UitkFigureAspectRatioType.R16_9} />
          </ViewMedium>
        </Viewport>
        <UitkCardLink>
          <a href={tile.url} aria-label={label} target={tile.target || '_self'}>
            {tile.header}
            {tile.ariaNewWindow && <span className="is-visually-hidden">{tile.ariaNewWindow}</span>}
          </a>
        </UitkCardLink>
      </UitkCard>
    </UitkLayoutGridItem>
  );
};

TileCard.displayName = 'TileCard';

interface MultiTileLogoProps {
  logo?: string;
  logoAltText?: string;
}

const MultiTileLogo: React.FunctionComponent<MultiTileLogoProps> = props => {
  const { logo, logoAltText } = props;

  return (
    logo && (
      <UitkLayoutFlexItem grow={0} minWidth="twenty_four">
        <div>
          <UitkFigure ratio={UitkFigureAspectRatioType.R21_9} imageFit="contain">
            <UitkImage alt={logoAltText} src={logo} />
          </UitkFigure>
        </div>
      </UitkLayoutFlexItem>
    )
  );
};

MultiTileLogo.displayName = 'MultiTileLogo';

export const MesoDexMultiTile: React.FunctionComponent<DexMultiTileModel> = props => {
  const { header, description, logo, logoAltText, tiles, sponsoredLabel } = props;

  return (
    <>
      <UitkSpacingHr blockMargin={'six'} />
      <Viewport>
        <ViewSmall>
          <div>
            <UitkLayoutFlex space="twelve" wrap="nowrap" justifyContent="space-between" alignContent="start">
              <MultiTileLogo logo={logo} logoAltText={logoAltText} />
              <UitkLayoutFlexItem grow={0} minWidth="zero" alignSelf="start">
                <div>
                  <UitkSponsoredBadge hasBorder={true} text={sponsoredLabel} />
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
            <UitkSpacing margin={{ block: 'two' }}>
              <UitkLayoutFlex space="half" direction="column">
                <UitkLayoutFlexItem grow={1} minWidth="full_width">
                  <UitkHeading tag="h1" size={5}>
                    {header}
                  </UitkHeading>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem grow={1} minWidth="full_width" alignSelf="start">
                  <UitkSpacing margin={{ blockstart: 'half' }}>
                    <UitkText size={300}>{description}</UitkText>
                  </UitkSpacing>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkSpacing>
          </div>
        </ViewSmall>

        <ViewMedium>
          <div>
            <UitkSpacing margin={{ blockend: 'one' }}>
              <UitkLayoutFlex space="two" wrap="nowrap" justifyContent="space-between" alignContent="start">
                <MultiTileLogo logo={logo} logoAltText={logoAltText} />
                <UitkLayoutFlexItem minWidth="full_width">
                  <UitkLayoutFlex direction="column">
                    <UitkLayoutFlexItem grow={1} minWidth="full_width">
                      <UitkHeading tag="h1" size={5}>
                        {header}
                      </UitkHeading>
                    </UitkLayoutFlexItem>
                    <UitkLayoutFlexItem grow={1} minWidth="full_width" alignSelf="start">
                      <UitkSpacing margin={{ blockstart: 'half' }}>
                        <UitkText size={300}>{description}</UitkText>
                      </UitkSpacing>
                    </UitkLayoutFlexItem>
                  </UitkLayoutFlex>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem grow={0} alignSelf="start">
                  <div>
                    <UitkSponsoredBadge hasBorder={true} text={sponsoredLabel} />
                  </div>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkSpacing>
          </div>
        </ViewMedium>
      </Viewport>

      <UitkLayoutGrid columns={3} space="two">
        {tiles.map((tile, i) => (
          <TileCard tile={tile} index={i} />
        ))}
      </UitkLayoutGrid>
      <UitkSpacingHr blockMargin={'six'} />
    </>
  );
};
MesoDexMultiTile.displayName = 'MesoDexMultiTile';
